import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as Alert } from '../../../../assets/icons/alert-icon.svg';
import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  HistoricalFaultInterFace,
  RequestHistoricalFaultInterFace,
} from '../../../../interface/historical-faults-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  disableHistoricalFaultFiltersApplied,
  fetchHistoricalFault,
  fetchListHistoricalFaultFilters,
  updateHistoricalFaultFilters,
  setSortData,
} from '../../../../redux/slices/HistoricalFaultSlice';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import DeviceSidePanel from '../../../../shared-components/table-side-panel/DeviceSidePanel';
import { useTableSidePanel } from '../../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import LocationSidePanel from '../../../../shared-components/table-side-panel/LocationSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../../../shared-functions/ConvertUTCtoHHMM';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import FormatUtcString from '../../../../shared-functions/FormatUtcString';
import stationManagementApi from '../../../../utils/api/location-management';

/**
 * Historical Faults
 * @returns {any} The rendered component
 */
function HistoricalFaults() {
  const { t, i18n } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    HistoricalFault,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.HistoricalFault);
  const { selectedSingleLocationPerformanceList, selectedChargingPoint } = useAppSelector(
    (state) => state.locationManagement,
  );
  /**
   * Handle the fetch the data in api function
   * @param {any} data The request data
   */
  const fetchData = (data: RequestHistoricalFaultInterFace) => {
    if (!loading) {
      dispatch(
        fetchHistoricalFault({
          ...data,
          chargingStation: selectedChargingPoint?.id,
          chargingPointSn: selectedSingleLocationPerformanceList?.id,
        }),
      );
    }
  };
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchHistoricalFault({ ...queryParameters, page, limit, sortBy, sortField });
  };

  const { isEditMode, handleEditModeChange, handleSidePanelClose, renderSidePanelTopLeftContent } =
    useTableSidePanel({
      onRefresh: handleRefresh,
      sessions: HistoricalFault,
    });
  const column = [
    {
      label: t('date'),
      accessorKey: 'createdAt' as keyof HistoricalFaultInterFace,
      sortAllow: true,
      /**
       * Converting timestamp to UTC date
       * @param {any} stationActivity The activity object
       * @returns {any} The formatted date
       */
      customData: (stationActivity: HistoricalFaultInterFace) => (
        <p>{ConvertUTCtoDate(stationActivity.createdAt)}</p>
      ),
    },
    {
      label: t('time'),
      accessorKey: 'time' as keyof HistoricalFaultInterFace,
      sortAllow: true,
      /**
       * Converting timestamp to UTC time
       * @param {any} stationActivityTime The activity object
       * @returns {any} The formatted time
       */
      customData: (stationActivityTime: HistoricalFaultInterFace) => (
        <p>{FormatUtcString(ConvertUTCtoHHMM(stationActivityTime.createdAt))}</p>
      ),
    },
    {
      label: t('stationName'),
      accessorKey: 'stationName' as keyof HistoricalFaultInterFace,
      sortAllow: true,
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStation',
      sidePanelComponent: (
        <LocationSidePanel
          fetchIdFrom="queryParams"
          initialIsEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => handleEditModeChange(false)}
        />
      ),
      disableNewTab: true,
    },
    {
      label: t('deviceSn'),
      accessorKey: 'chargingPointSn' as keyof HistoricalFaultInterFace,
      sortAllow: true,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: (
        <DeviceSidePanel
          fetchIdFrom="queryParams"
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
        />
      ),
      disableNewTab: true,
    },
    {
      label: t('deviceName'),
      accessorKey: 'chargingPointName' as keyof HistoricalFaultInterFace,
      sortAllow: true,
    },
    {
      label: t('title'),
      accessorKey: 'title' as keyof HistoricalFaultInterFace,
      sortAllow: true,
    },
    {
      label: t('faults'),
      accessorKey: 'message' as keyof HistoricalFaultInterFace,
      sortAllow: true,
      /**
       * Document view in table
       * @param {any} historicalFault The fault object
       * @returns {any} The rendered cell
       */
      customData: (historicalFault: HistoricalFaultInterFace) => (
        <div className="document-in-station-table">
          <div className="document-container">
            <span>
              <Alert />
            </span>
            <p className="table-row-each-data">{historicalFault.message}</p>
          </div>
        </div>
      ),
    },
  ];

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'createdAt' },
    { label: t('time'), accessorKey: 'time' },
    { label: t('stationName'), accessorKey: 'stationName' },
    { label: t('deviceSnShort'), accessorKey: 'chargingPointSn' },
    { label: t('deviceName'), accessorKey: 'chargingPointName' },
    { label: t('title'), accessorKey: 'title' },
    { label: t('faults'), accessorKey: 'message' },
  ];

  /**
  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);

    stationManagementApi
      .fetchHistoricalFaultsListExport({
        chargingStation: selectedChargingPoint?.id,
        chargingPointSn: selectedSingleLocationPerformanceList?.id,
        ...queryParameters,
      })
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: HistoricalFaultInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'createdAt':
                return ConvertUTCtoDate(row?.createdAt) || t('unavailable');
              case 'time':
                return ConvertUTCtoHHMM(row?.createdAt) || t('unavailable');
              default:
                return (row as any)[col?.accessorKey] || t('unavailable');
            }
          }),
        );
        if (type === 'csv') {
          exportCSV(headers, rows, 'charging-location-approved.csv');
        } else {
          exportPDF(headers, rows, 'charging-location-approved.pdf');
        }
        setExportLoader(false);
      })
      .catch((error: any) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('somethingWentWrong'),
            status: error?.response?.status || 500,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };
  /**
   * Table pagination function
   * @param {any} value The pagination value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...value,
      ...queryParameters,
      sortBy,
      sortField,
    });
  };

  /**
   * Function for sorting table data
   * @param {object} sortData The sort configuration
   * @param {any} sortData.sortBy - The sortBy prop
   * @param {any} sortData.sortField - The sortField prop
   */
  const handleSortFunction = (sortData: { sortBy: 'asc' | 'desc'; sortField: string }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  useEffect(() => {
    if (isFilterUpdated) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page,
        limit,
        ...queryParameters,
      });
      dispatch(disableHistoricalFaultFiltersApplied());
    }
  }, [filters, selectedSingleLocationPerformanceList]);

  useEffect(() => {
    if (!selectedSingleLocationPerformanceList?.id) return;

    const currentId = selectedSingleLocationPerformanceList.id;
    const lastId = sessionStorage.getItem('HistoricalFaultFiltersAppliedList'); // string | null

    // If same id as last time -> do nothing
    if (lastId !== currentId || filters.length === 1) {
      dispatch(
        fetchListHistoricalFaultFilters({
          chargingStation: selectedChargingPoint?.id,
          chargingPointSn: selectedSingleLocationPerformanceList?.id,
        }),
      );
    }
    if (HistoricalFault.length === 0 || lastId !== currentId) {
      fetchData({
        page,
        limit,
      });
      dispatch(disableHistoricalFaultFiltersApplied());
    }
    // Save this id so next time we can compare
    sessionStorage.setItem('HistoricalFaultFiltersAppliedList', currentId);
  }, [selectedChargingPoint?.id, filters, selectedSingleLocationPerformanceList]);

  return (
    <div dir={i18n.dir()}>
      <CustomTable
        loading={loading}
        limit={limit}
        header={column}
        data={HistoricalFault}
        totalPages={totalPages}
        totalResults={totalResults}
        pageLimitChange={pageNateLimitChange}
        height={400}
        version="v2"
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        exportData={HistoricalFault}
        exportLoader={exportLoader}
        filters={filters}
        filtersToDispatch={updateHistoricalFaultFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchListHistoricalFaultFilters}
        refershCurrentPages={handleRefresh}
        serialNo
        sortData={{
          sortBy,
          sortField,
        }}
        handleSortChange={handleSortFunction}
        disableHeader={false}
        leftTopContent={<> </>}
        onHiddenColumnsChange={setHiddenColumns}
        sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
        onSidePanelClose={() => {
          handleSidePanelClose();
        }}
      />
    </div>
  );
}

export default HistoricalFaults;
